// src/components/marketing/SocialPublishCalendar.tsx
// Track E3 — social publish calendar view.
//
// Loads `/admin/posts/calendar?start_date=&end_date=` (dense day buckets,
// already sparse-filtered by the backend) and paints a responsive grid
// of day cards. Each card shows the posts scheduled for that day with
// platform / status / time / caption preview / thumbnail.
//
// Toolbar offers: prev/next 7d, refresh, and a primary "Nuevo post"
// button that opens the `<ComposePost/>` modal. Clicking a specific day
// also opens compose with that date pre-selected.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  Clock, ExternalLink, Facebook, Image as ImageIcon, Instagram, Loader2, Pencil,
  PlusCircle, RefreshCw, Send, Sun, Trash2, XCircle,
} from 'lucide-react';

import {
  ApiError, deletePost, getCalendar, getPost,
  publishNow, type CalendarDay, type Platform, type PostStatus,
  type ScheduledPost, type ScheduledPostDetail,
} from '@/lib/api_social_publish';
import { BusinessSelector } from '@/components/businesses/BusinessSelector';
import { useActiveBusiness } from '@/context/BusinessContext';
import { ComposePost } from './ComposePost';
import {
  CancelScheduledPost, EditScheduledPost, findSiblings, isEditable,
} from './EditScheduledPost';

type Notifier = Parameters<typeof ComposePost>[0]['addNotification'];

interface Props {
  addNotification?: Notifier;
}

const WINDOW_DAYS = 14; // 2-week horizon is long enough to plan without losing detail

export function SocialPublishCalendar({ addNotification }: Props) {
  const { activeId: activeBusinessId } = useActiveBusiness();
  const [days, setDays] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDate, setComposeDate] = useState<Date | null>(null);
  const [pending, setPending] = useState<Record<number, 'publish' | 'delete'>>({});
  const [mediaCache, setMediaCache] = useState<Record<number, ScheduledPostDetail>>({});
  // Post en edición: `edit` (modal completo), `today` (selector rápido de
  // hora para hoy) o `cancel` (confirmación borrador/eliminar).
  const [editing, setEditing] = useState<{ post: ScheduledPost; mode: 'edit' | 'today' | 'cancel' } | null>(null);
  const allPosts = useMemo(() => days.flatMap((d) => d.posts), [days]);

  const { start, end } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startD = new Date(today);
    startD.setDate(startD.getDate() + offset);
    const endD = new Date(startD);
    endD.setDate(endD.getDate() + (WINDOW_DAYS - 1));
    return { start: startD, end: endD };
  }, [offset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getCalendar({
        start_date: toIsoDate(start),
        end_date: toIsoDate(end),
        business_id: activeBusinessId || undefined,
      });
      setDays(rows);
      // Reset media cache — thumbnails are resolved lazily per visible post
      // so stale entries from a different window don't flash into view.
      setMediaCache({});
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error cargando calendario');
      setError(msg);
      addNotification?.({ type: 'error', title: 'No se pudo cargar el calendario', message: msg });
    } finally {
      setLoading(false);
    }
  }, [start, end, addNotification, activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  // Resolve first-media thumbnails lazily so the calendar stays fast on
  // windows with 20+ posts. One extra GET per unique post id; cached in
  // `mediaCache` so we never re-fetch within the same window.
  useEffect(() => {
    let cancelled = false;
    const missing = days
      .flatMap((d) => d.posts)
      .filter((p) => !(p.id in mediaCache));
    if (missing.length === 0) return;
    (async () => {
      for (const post of missing) {
        try {
          const detail = await getPost(post.id);
          if (cancelled) return;
          setMediaCache((prev) => ({ ...prev, [post.id]: detail }));
        } catch {
          /* 404 / network — silent, row will render without thumbnail */
        }
      }
    })();
    return () => { cancelled = true; };
  }, [days, mediaCache]);

  const handlePublishNow = async (post: ScheduledPost) => {
    setPending((p) => ({ ...p, [post.id]: 'publish' }));
    try {
      await publishNow(post.id);
      addNotification?.({
        type: 'success',
        title: 'Publicación encolada',
        message: 'El beat de A8 lo recoge en ≤60s.',
        duration: 4000,
      });
      load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      addNotification?.({ type: 'error', title: 'No se pudo publicar', message: msg });
    } finally {
      setPending((p) => { const { [post.id]: _d, ...rest } = p; return rest; });
    }
  };

  const handleDelete = async (post: ScheduledPost) => {
    const ok = window.confirm(`¿Borrar el post "${post.caption.slice(0, 60) || '(sin texto)'}"?`);
    if (!ok) return;
    setPending((p) => ({ ...p, [post.id]: 'delete' }));
    try {
      await deletePost(post.id);
      addNotification?.({ type: 'success', title: 'Post eliminado' });
      load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      addNotification?.({ type: 'error', title: 'No se pudo borrar', message: msg });
    } finally {
      setPending((p) => { const { [post.id]: _d, ...rest } = p; return rest; });
    }
  };

  const openCompose = (day?: CalendarDay) => {
    setComposeDate(day ? new Date(`${day.date}T12:00:00`) : null);
    setComposeOpen(true);
  };

  return (
    <div className="o-card p-5">
      <header className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <CalendarIcon size={18} />
          Calendario de publicaciones
        </h1>
        <div className="flex-1" />

        <div className="flex items-center rounded-md bg-white/5 border border-white/10 text-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setOffset((o) => o - WINDOW_DAYS)}
            className="px-2 py-1.5 hover:bg-white/10"
            title="Ventana anterior"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="px-3 text-white/70">
            {formatRange(start, end)}
          </span>
          <button
            type="button"
            onClick={() => setOffset((o) => o + WINDOW_DAYS)}
            className="px-2 py-1.5 hover:bg-white/10"
            title="Ventana siguiente"
          >
            <ChevronRight size={14} />
          </button>
          {offset !== 0 && (
            <button
              type="button"
              onClick={() => setOffset(0)}
              className="px-2 py-1.5 hover:bg-white/10 border-l border-white/10 text-xs text-white/60"
              title="Ir a hoy"
            >
              Hoy
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <BusinessSelector compact />
        </div>

        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="o-btn-ghost p-1.5 rounded-md"
          title="Refrescar"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>

        <button
          type="button"
          onClick={() => openCompose()}
          className="px-3 py-1.5 rounded-lg bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 flex items-center gap-1 text-sm font-medium"
        >
          <PlusCircle size={14} />
          Nuevo post
        </button>
      </header>

      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {loading && days.length === 0 ? (
        <div className="h-40 flex items-center justify-center opacity-60">
          <Loader2 className="animate-spin" size={20} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {days.map((d) => (
            <DayCard
              key={d.date}
              day={d}
              mediaCache={mediaCache}
              pending={pending}
              onPublish={handlePublishNow}
              onDelete={handleDelete}
              onEdit={(post, mode) => setEditing({ post, mode })}
              onAddClick={() => openCompose(d)}
            />
          ))}
        </div>
      )}

      {editing && editing.mode !== 'cancel' && (
        <EditScheduledPost
          key={`${editing.post.id}-${editing.mode}`}
          post={editing.post}
          siblings={findSiblings(editing.post, allPosts)}
          quickToday={editing.mode === 'today'}
          addNotification={addNotification}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {editing && editing.mode === 'cancel' && (
        <CancelScheduledPost
          post={editing.post}
          siblings={findSiblings(editing.post, allPosts)}
          addNotification={addNotification}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}

      {composeOpen && (
        <ComposePost
          defaultDate={composeDate}
          addNotification={addNotification}
          onClose={() => setComposeOpen(false)}
          onCreated={() => {
            setComposeOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Day card
// ═══════════════════════════════════════════════════════════════

function DayCard({
  day,
  mediaCache,
  pending,
  onPublish,
  onDelete,
  onEdit,
  onAddClick,
}: {
  day: CalendarDay;
  mediaCache: Record<number, ScheduledPostDetail>;
  pending: Record<number, 'publish' | 'delete'>;
  onPublish: (p: ScheduledPost) => void;
  onDelete: (p: ScheduledPost) => void;
  onEdit: (p: ScheduledPost, mode: 'edit' | 'today' | 'cancel') => void;
  onAddClick: () => void;
}) {
  const today = toIsoDate(new Date());
  const isToday = day.date === today;
  const isPast = day.date < today;

  return (
    <div
      className={`rounded-lg border overflow-hidden flex flex-col min-h-[160px] ${
        isToday
          ? 'border-[var(--acc1,#00e5ff)]/40 bg-[var(--acc1,#00e5ff)]/5'
          : isPast
          ? 'border-white/5 bg-white/[0.02]'
          : 'border-white/10 bg-white/5'
      }`}
    >
      <div className={`px-3 py-2 border-b flex items-center justify-between ${
        isToday ? 'border-[var(--acc1,#00e5ff)]/30' : 'border-white/10'
      }`}>
        <div>
          <div className="text-xs uppercase tracking-wider text-white/40">
            {formatDayLabel(day.date)}
          </div>
          <div className={`text-sm font-semibold ${isToday ? 'text-[var(--acc1,#00e5ff)]' : ''}`}>
            {formatDayNumber(day.date)}
          </div>
        </div>
        <button
          type="button"
          onClick={onAddClick}
          title="Crear post para este día"
          className="p-1 rounded-md text-white/50 hover:text-white hover:bg-white/10"
        >
          <PlusCircle size={14} />
        </button>
      </div>

      <div className="p-2 flex-1 space-y-2">
        {day.posts.length === 0 ? (
          <div className="text-xs text-white/30 text-center py-4">
            Sin posts.
          </div>
        ) : (
          day.posts.map((post) => (
            <PostItem
              key={post.id}
              post={post}
              thumbnail={mediaCache[post.id]?.media[0]?.thumbnail_url || mediaCache[post.id]?.media[0]?.media_url}
              busy={pending[post.id]}
              onPublish={() => onPublish(post)}
              onDelete={() => onDelete(post)}
              onEdit={(mode) => onEdit(post, mode)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function PostItem({
  post,
  thumbnail,
  busy,
  onPublish,
  onDelete,
  onEdit,
}: {
  post: ScheduledPost;
  thumbnail?: string;
  busy?: 'publish' | 'delete';
  onPublish: () => void;
  onDelete: () => void;
  onEdit: (mode: 'edit' | 'today' | 'cancel') => void;
}) {
  const canPublish = isEditable(post);
  // Publicados / publicando: solo lectura.
  const readOnly = post.status === 'published' || post.status === 'publishing';
  const permalink = publishedLink(post);
  const hour = post.scheduled_at
    ? new Date(post.scheduled_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="rounded-md bg-white/5 border border-white/10 p-2 flex gap-2 group">
      {thumbnail ? (
        <img
          src={thumbnail}
          alt=""
          loading="lazy"
          className="w-12 h-12 rounded-md object-cover shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-md bg-white/5 flex items-center justify-center text-white/30 shrink-0">
          <ImageIcon size={14} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-xs text-white/60">
          <PlatformIcon platform={post.platform} />
          <span className="uppercase tracking-wider">{post.post_type}</span>
          {hour && (
            <>
              <Clock size={10} className="ml-1" />
              <span>{hour}</span>
            </>
          )}
          <span className="flex-1" />
          <StatusBadge status={post.status} />
        </div>
        <div className="text-xs mt-1 line-clamp-2 text-white/80">
          {post.caption || <span className="text-white/40">(sin texto)</span>}
        </div>
        {post.error_message && (
          <div className="text-[10px] text-red-300/80 mt-1 line-clamp-2" title={post.error_message}>
            {post.error_message}
          </div>
        )}
        {readOnly ? (
          post.status === 'published' && (
            <div className="mt-1 text-[10px] text-white/50 flex items-center gap-1">
              {permalink ? (
                <a
                  href={permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-white underline-offset-2 hover:underline"
                >
                  <ExternalLink size={10} /> Ver publicación
                </a>
              ) : post.meta_post_id ? (
                <span title="Meta no devuelve permalink para este post">ID {post.meta_post_id}</span>
              ) : null}
            </div>
          )
        ) : (
        <div className="flex items-center gap-1 mt-1 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <RowAction
            Icon={Pencil}
            label="Editar"
            onClick={() => onEdit('edit')}
            disabled={!!busy}
          />
          <RowAction
            Icon={Sun}
            label="Mover a hoy"
            onClick={() => onEdit('today')}
            disabled={!!busy}
          />
          <RowAction
            Icon={Send}
            label="Publicar ahora"
            onClick={onPublish}
            disabled={!canPublish || !!busy}
            busy={busy === 'publish'}
          />
          <RowAction
            Icon={XCircle}
            label="Cancelar publicación"
            onClick={() => onEdit('cancel')}
            disabled={!!busy}
          />
          <RowAction
            Icon={Trash2}
            label="Eliminar"
            danger
            onClick={onDelete}
            disabled={!!busy}
            busy={busy === 'delete'}
          />
        </div>
        )}
      </div>
    </div>
  );
}

function RowAction({
  Icon, label, onClick, disabled, busy, danger,
}: {
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`p-1 rounded-md transition-colors ${
        disabled
          ? 'opacity-30 cursor-not-allowed'
          : danger
          ? 'hover:bg-red-500/20 text-red-300'
          : 'hover:bg-white/10 text-white/70 hover:text-white'
      }`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
    </button>
  );
}

// ── Shared badges ────────────────────────────────────────────

const STATUS_STYLES: Record<PostStatus, { bg: string; label: string }> = {
  draft:      { bg: 'bg-white/10 text-white/70',          label: 'Borrador' },
  scheduled:  { bg: 'bg-sky-500/15 text-sky-300',         label: 'Programado' },
  publishing: { bg: 'bg-amber-500/15 text-amber-300',     label: 'Publicando' },
  published:  { bg: 'bg-emerald-500/15 text-emerald-300', label: 'Publicado' },
  failed:     { bg: 'bg-red-500/15 text-red-300',         label: 'Falló' },
};

function StatusBadge({ status }: { status: PostStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium ${s.bg}`}>
      {s.label}
    </span>
  );
}

function PlatformIcon({ platform }: { platform: Platform }) {
  if (platform === 'instagram') return <Instagram size={10} className="text-pink-300" />;
  return <Facebook size={10} className="text-sky-300" />;
}

/** El backend solo expone `meta_post_id` (no permalink). Para Facebook el id
 *  `<page>_<post>` resuelve en facebook.com/<id>; para Instagram el media id
 *  no es una URL pública, así que no se arma link. */
function publishedLink(post: ScheduledPost): string | null {
  if (!post.meta_post_id) return null;
  if (post.platform === 'facebook') return `https://www.facebook.com/${post.meta_post_id}`;
  return null;
}

// ── Date helpers ─────────────────────────────────────────────

function toIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatRange(start: Date, end: Date): string {
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatDayLabel(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  } catch { return ''; }
}

function formatDayNumber(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
}

export default SocialPublishCalendar;
